import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { RoiStore, RoiChangeStore, RoiStatsStore } from '../../stores';
import { ListChangedRoisUseCase, RevertChangeUseCase, RevertChangesResult } from '@analysis/use-cases';
import { LabelingService, LabelOptions, Roi } from '@analysis/domain';
import { EMPTY, from, MonoTypeOperatorFunction, Observable, of, pipe, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { ItemSelectionStore, SnackbarService } from '@shared/ui';
import { ProtocolStore } from '@telespot/protocols/ui';

export const REVERT_CHANGE_USE_CASE = new InjectionToken<RevertChangeUseCase>('REVERT_CHANGE_USE_CASE');
export const LIST_CHANGED_ROIS_USE_CASE = new InjectionToken<ListChangedRoisUseCase>('LIST_CHANGED_ROIS_USE_CASE');

const SNACKBAR_DEBOUNCE_MS = 300;

@Injectable()
export class RoiRevertEffects {
  private snackbarRef: MatSnackBarRef<unknown> | undefined;

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly roiStatsStore: RoiStatsStore,
    private readonly protocolStore: ProtocolStore,
    @Optional() private readonly itemSelectionStore: ItemSelectionStore,
    @Inject(REVERT_CHANGE_USE_CASE)
    private readonly revertChangeUseCase: RevertChangeUseCase,
    @Inject(LIST_CHANGED_ROIS_USE_CASE)
    private readonly listChangedRoisUseCase: ListChangedRoisUseCase,
    private readonly snackbarService: SnackbarService
  ) {
    this.wireSnackbar(this.roiChangeStore.revertingChanges$);
  }

  readonly revertAllChanges = this.roiStore.effect((trigger$: Observable<{ sampleId: string; creatorId: string }>) =>
    trigger$.pipe(
      this.dropWhenApplyingChanges(),
      switchMap((payload) => {
        this.roiChangeStore.setRevertingChanges(true);
        return from(this.listChangedRoisUseCase.execute(payload)).pipe(
          concatLatestFrom(() => this.protocolStore.options$),
          switchMap(([rois, options]) => from(this.revertChangeUseCase.execute({ rois: this.filterInputs(rois, options) }))),
          tap((result) => this.applyRevertResult(result)),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setRevertingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  readonly revertSelectedRoiChanges = this.roiStore.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      this.dropWhenApplyingChanges(),
      concatLatestFrom(() => [
        this.roiChangeStore.changedRois$,
        this.roiStore.rois$,
        this.itemSelectionStore.selectedItemsArray$,
        this.protocolStore.options$,
      ]),
      map(([, changedRois, rois, selectedItems, options]) => ({
        rois: selectedItems
          .filter((id) => changedRois.has(id))
          .map((id) => rois[id])
          .filter(Boolean),
        options,
      })),
      switchMap(({ rois, options }) => {
        this.roiChangeStore.setRevertingChanges(true);
        return from(this.revertChangeUseCase.execute({ rois: this.filterInputs(rois, options) })).pipe(
          tap((result) => this.applyRevertResult(result)),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setRevertingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  readonly revertRoiChanges = this.roiStore.effect((roiId$: Observable<string>) =>
    roiId$.pipe(
      this.dropWhenApplyingChanges(),
      concatLatestFrom(() => [this.roiStore.rois$, this.protocolStore.options$]),
      map(([roiId, rois, options]) => ({ roi: rois[roiId], options })),
      filter((input) => !!input.roi),
      switchMap(({ roi, options }) => {
        this.roiChangeStore.setRevertingChanges(true);
        return from(this.revertChangeUseCase.execute({ rois: this.filterInputs([roi], options) })).pipe(
          tap((result) => this.applyRevertResult(result)),
          catchError((error) => {
            this.roiStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.roiChangeStore.setRevertingChanges(false)),
          map((): void => undefined)
        );
      })
    )
  );

  private readonly wireSnackbar = this.roiStore.effect((flag$: Observable<boolean>) =>
    flag$.pipe(
      distinctUntilChanged(),
      switchMap((active) => (active ? timer(SNACKBAR_DEBOUNCE_MS).pipe(map(() => true)) : of(false))),
      tap((active: boolean) => {
        this.snackbarRef?.dismiss();
        this.snackbarRef = undefined;
        if (!active) return;
        this.snackbarRef = this.snackbarService.show('analysis.reverting_changes', {
          duration: 0,
          dismissible: false,
        });
      })
    )
  );

  private filterInputs(rois: Roi[], options: LabelOptions): Roi[] {
    return rois.map((roi) => LabelingService.filterLabels(roi, options));
  }

  private applyRevertResult({ revertedChanges, revertedRois, counterDeltas, roisToDeleteIds }: RevertChangesResult): void {
    // Counter deltas must land before the change map empties: if unsynced flips
    // to false while labelCounts still include the reverted edits, observers
    // combining the two stores see a transient "projection behind && nothing
    // unsynced" state (same ordering as RoiSyncEffects.syncChanges).
    this.roiStatsStore.applyCounterDeltas(counterDeltas);
    this.roiChangeStore.addChanges(revertedChanges);
    this.roiStore.upsertRois(revertedRois);
    if (roisToDeleteIds.length > 0) this.roiStore.removeRoisByIds(roisToDeleteIds);
  }

  private dropWhenApplyingChanges<T>(): MonoTypeOperatorFunction<T> {
    return pipe(
      concatLatestFrom(() => [this.roiChangeStore.applyingChanges$, this.roiChangeStore.revertingChanges$]),
      filter(([, applying, reverting]: [T, boolean, boolean]) => !applying && !reverting),
      map(([payload]: [T, boolean, boolean]) => payload)
    );
  }
}
